import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { PhilanthropiesAPI, PhilanthropyPayload, PhilanthropyCategory, Philanthropy } from '../services/philanthropies.ts';
import { applyCurrencyMask, parseCurrency, formatCurrencyInput } from '../utils/currency.ts';
import { ArrowLeftIcon, CheckIcon, DocumentTextIcon, CurrencyDollarIcon, HeartIcon } from '@heroicons/react/24/outline';

const EditPhilanthropy: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();

  const { data: item, isLoading } = useQuery<Philanthropy>(['philanthropy', id], () => PhilanthropiesAPI.get(id!), {
    enabled: !!id,
  });

  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<PhilanthropyCategory>('education');
  const [amountDisplay, setAmountDisplay] = useState<string>('');
  const [donationDate] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (item) {
      setDescription(item.description || '');
      setCategory(item.category);
      setAmountDisplay(formatCurrencyInput(Number(item.amount || 0)));
      // Data derivada do último lançamento; não editável aqui
    }
  }, [item]);

  const updateMutation = useMutation(async (payload: PhilanthropyPayload) => PhilanthropiesAPI.update(id!, payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['philanthropies']);
      navigate('/philanthropies');
    },
  });

  const validate = (): boolean => {
    const nextErrors: Record<string, string> = {};
    if (!description || !description.trim()) {
      nextErrors.description = 'Descrição é obrigatória';
    }
    if (!category) {
      nextErrors.category = 'Categoria é obrigatória';
    }
    const amount = parseCurrency(amountDisplay);
    if (!amount || amount <= 0) {
      nextErrors.amount = 'Valor deve ser maior que zero';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    const payload: PhilanthropyPayload = {
      description: description.trim(),
      category,
      amount: parseCurrency(amountDisplay) || 0,
      donationDate: donationDate ? donationDate : null,
    };
    try {
      await updateMutation.mutateAsync(payload);
    } catch (err) {
      console.error('Erro ao atualizar doação:', err);
    }
  };

  if (isLoading || !item) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/philanthropies/${id}`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar
          </button>

          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <HeartIcon className="h-8 w-8 mr-2 text-pink-600" />
            Editar Doação
          </h1>
          <p className="text-gray-600 mt-2">Atualize os dados da doação</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-primary-600" />
              Informações Básicas
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Descrição *</label>
                <input
                  type="text"
                  value={description}
                  onChange={e => { setDescription(e.target.value); if (errors.description) setErrors(prev => ({ ...prev, description: '' })); }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                />
                {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Categoria *</label>
                <select
                  value={category}
                  onChange={e => { setCategory(e.target.value as PhilanthropyCategory); if (errors.category) setErrors(prev => ({ ...prev, category: '' })); }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.category ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="education">Educação</option>
                  <option value="health">Saúde</option>
                  <option value="environment">Meio Ambiente</option>
                  <option value="other">Outros</option>
                </select>
                {errors.category && <p className="mt-1 text-sm text-red-600">{errors.category}</p>}
              </div>

              {/* Data agora é derivada dos lançamentos filhos; removida do formulário pai */}
            </div>
          </div>

          {/* Valores */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <CurrencyDollarIcon className="h-6 w-6 mr-2 text-primary-600" />
              Valor disponível para doação
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Valor disponível para doação (R$) *</label>
                <input
                  type="text"
                  value={amountDisplay}
                  onChange={e => {
                    const masked = applyCurrencyMask(e.target.value);
                    setAmountDisplay(masked);
                    if (errors.amount) setErrors(prev => ({ ...prev, amount: '' }));
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${errors.amount ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                />
                {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(`/philanthropies/${id}`)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={updateMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center disabled:opacity-60"
            >
              <CheckIcon className="h-5 w-5 mr-2" />
              {updateMutation.isLoading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPhilanthropy;
